import React, { useState } from "react";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip,
} from "recharts";
import { Lock, LockOpen, Pencil, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ciApi } from "../ciApi";
import { ScoreRing, RagBadge, ConfBadge, TrendBadge, Panel, Kicker } from "../ciWidgets";

const Glance = ({ label, value }) => (
  <div className="rounded-lg border border-white/8 bg-[#060d18] px-4 py-3">
    <div className="text-[10px] tracking-[0.16em] uppercase text-white/40 font-semibold mb-1">{label}</div>
    <div className="text-[13px] text-white/80 leading-snug">{value || "—"}</div>
  </div>
);

export const OverviewTab = ({ report, onReload }) => {
  const [editingVerdict, setEditingVerdict] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const execLocked = !!report.locks?.executive_summary?.locked;

  const saveVerdict = async () => {
    setBusy(true);
    try {
      await ciApi.patch(`/reports/${report.id}/edit`, { target: "verdict", value: draft });
      toast.success("Board verdict updated");
      setEditingVerdict(false);
      onReload && (await onReload());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const toggleExecLock = async () => {
    try {
      await ciApi.patch(`/reports/${report.id}/locks`, { section: "executive_summary", locked: !execLocked });
      toast.success(execLocked ? "Executive summary unlocked" : "Executive summary locked & approved");
      onReload && (await onReload());
    } catch {
      toast.error("Failed to update the lock");
    }
  };

  const dims = Object.values(report.dimensions || {});
  const radarData = dims.map((d) => ({
    dim: d.name.replace(" & ", " · ").split(" ").slice(0, 2).join(" "),
    fullName: d.name,
    score: d.score ?? 0,
    indicative: d.score == null,
  }));
  const g = report.company_at_a_glance || {};
  const si = report.strategic_intent || {};
  const ex = report.executive_summary || {};
  const overall = report.overall || {};

  return (
    <div className="space-y-6" data-testid="ci-tab-overview">
      {/* Verdict strip */}
      <Panel className="p-8">
        <div className="grid lg:grid-cols-[auto_1fr_1fr] gap-8 items-center">
          <div className="flex flex-col items-center">
            <ScoreRing score={overall.score} size={170} label="Enterprise value" />
            <div className="flex items-center gap-2 mt-3">
              <RagBadge rag={overall.rag} />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Kicker>Board verdict</Kicker>
              <button
                onClick={toggleExecLock}
                data-testid="ci-exec-lock-btn"
                title={execLocked ? "Unlock executive summary" : "Lock & approve executive summary"}
                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold transition-colors ${
                  execLocked
                    ? "border-amber-400/50 text-amber-300 bg-amber-400/10"
                    : "border-white/20 text-white/45 hover:text-emerald-300 hover:border-emerald-400/50"
                }`}
              >
                {execLocked ? <Lock className="w-3 h-3" /> : <LockOpen className="w-3 h-3" />}
                {execLocked ? "Locked" : "Lock"}
              </button>
              {!editingVerdict && (
                <button
                  onClick={() => {
                    if (execLocked) {
                      toast.error("Executive summary is locked — unlock it to edit");
                      return;
                    }
                    setDraft(overall.verdict || "");
                    setEditingVerdict(true);
                  }}
                  data-testid="ci-edit-verdict-btn"
                  className={`inline-flex items-center gap-1 text-[11px] transition-colors ${execLocked ? "text-white/25 cursor-not-allowed" : "text-white/45 hover:text-teal-300"}`}
                >
                  <Pencil className="w-3 h-3" /> Edit
                </button>
              )}
            </div>
            {editingVerdict ? (
              <div className="mb-4">
                <textarea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  data-testid="ci-verdict-textarea"
                  className="w-full min-h-[90px] rounded-lg bg-[#060d18] border border-teal-400/40 px-4 py-3 text-[15px] font-serif text-white leading-snug focus:outline-none focus:border-teal-300"
                />
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={saveVerdict}
                    disabled={busy || !draft.trim()}
                    data-testid="ci-save-verdict-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-teal-400 hover:bg-teal-300 disabled:opacity-40 text-[#06251f] font-bold text-[12px] px-4 py-1.5 transition-colors"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                  </button>
                  <button onClick={() => setEditingVerdict(false)} className="inline-flex items-center gap-1 text-[12px] text-white/50 hover:text-white transition-colors">
                    <X className="w-3.5 h-3.5" /> Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="font-serif text-xl sm:text-2xl leading-snug text-white mb-4" data-testid="ci-board-verdict">
                {overall.verdict || "—"}
                {overall.verdict_edited && <span className="ml-2 align-middle text-[10px] font-sans uppercase font-bold tracking-[0.12em] text-teal-300">Edited</span>}
              </div>
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-white/55">
              <span>Evidence quality <b className="text-teal-300">{overall.evidence_quality_score ?? overall.evidence_coverage}%</b></span>
              {overall.scored_weight != null && <span>Scored weight <b className="text-teal-300">{overall.scored_weight}%</b></span>}
              <span>Confidence <b className="text-teal-300 uppercase">{overall.confidence || "—"}</b></span>
              <span>Business model: {report.classification?.industry_classification || "—"}</span>
            </div>
            {overall.value_creation_potential && (
              <div className="mt-4 rounded-lg border border-amber-400/25 bg-amber-400/[0.06] px-4 py-3 text-[13px] text-amber-100/90">
                <b>Indicative value-creation potential:</b> {overall.value_creation_potential}
              </div>
            )}
          </div>
          <div className="h-64 min-w-0" data-testid="ci-overview-radar-container">
            <ResponsiveContainer width="100%" height={256} minWidth={0} initialDimension={{ width: 400, height: 256 }}>
              <RadarChart data={radarData} outerRadius="72%">
                <PolarGrid stroke="rgba(255,255,255,0.12)" />
                <PolarAngleAxis dataKey="dim" tick={{ fill: "rgba(255,255,255,0.55)", fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#0c1626", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#fff" }}
                  formatter={(v, n, p) => [`${v}/100${p.payload.indicative ? " (range midpoint)" : ""}`, p.payload.fullName]}
                />
                <Radar dataKey="score" stroke="#2dd4bf" fill="#2dd4bf" fillOpacity={0.25} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Panel>

      {/* Executive summary */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Panel className="p-7">
          <Kicker className="mb-4">Headline findings</Kicker>
          <ol className="space-y-3">
            {(ex.headline_findings || []).map((f, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-white/75 leading-relaxed">
                <span className="font-serif text-teal-300 tabular-nums shrink-0">{i + 1}.</span>
                {f}
              </li>
            ))}
          </ol>
        </Panel>
        <Panel className="p-7">
          <Kicker className="mb-4">Top board priorities</Kicker>
          <ol className="space-y-3">
            {(ex.board_priorities || []).map((f, i) => (
              <li key={i} className="flex gap-3 text-[14px] text-white/75 leading-relaxed">
                <span className="font-serif text-amber-300 tabular-nums shrink-0">{i + 1}.</span>
                {f}
              </li>
            ))}
          </ol>
        </Panel>
      </div>

      {report.purpose_lens && (report.purpose_lens.narrative || (report.purpose_lens.key_points || []).length > 0) && (
        <Panel className="p-7 border-amber-400/20" data-testid="ci-purpose-lens">
          <Kicker className="mb-1">{report.purpose_lens.title || `Purpose lens — ${report.purpose}`}</Kicker>
          <div className="text-[11px] text-white/40 mb-4">Written specifically for: {report.purpose}</div>
          {report.purpose_lens.narrative && (
            <p className="text-[14px] text-white/75 leading-relaxed mb-4" data-testid="ci-purpose-lens-narrative">{report.purpose_lens.narrative}</p>
          )}
          <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2.5">
            {(report.purpose_lens.key_points || []).map((p, i) => (
              <li key={i} className="flex gap-2.5 text-[13px] text-white/70 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300 mt-1.5 shrink-0" />
                {p}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        {[
          ["Key strengths", ex.top_strengths, "text-emerald-300"],
          ["Value leaks", ex.top_value_leaks, "text-amber-300"],
          ["Strategic risks", ex.top_risks, "text-red-300"],
        ].map(([title, items, cls]) => (
          <Panel key={title} className="p-6">
            <div className={`text-[11px] tracking-[0.2em] uppercase font-bold mb-3 ${cls}`}>{title}</div>
            <ul className="space-y-2.5">
              {(items || []).map((x, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-white/65 leading-relaxed">
                  <span className="w-1 h-1 rounded-full bg-white/30 mt-2 shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </Panel>
        ))}
      </div>

      {/* Company at a glance */}
      <Panel className="p-7">
        <Kicker className="mb-4">Company at a glance</Kicker>
        <p className="text-[14px] text-white/70 leading-relaxed mb-5 max-w-4xl">{g.business_profile}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Glance label="Revenue" value={g.revenue} />
          <Glance label="Profitability" value={g.profitability} />
          <Glance label="Employees" value={g.employees} />
          <Glance label="Geography" value={g.geography} />
          <Glance label="Products" value={g.products} />
          <Glance label="Customer segments" value={g.customer_segments} />
          <Glance label="Major assets" value={g.major_assets} />
          <Glance label="Ownership" value={g.ownership} />
        </div>
        <div className="grid md:grid-cols-2 gap-8 mt-6">
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50 mb-2.5">Strategic priorities</div>
            <ul className="space-y-2">
              {(g.strategic_priorities || []).map((x, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-white/65">
                  <span className="w-1 h-1 rounded-full bg-teal-300 mt-2 shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] tracking-[0.16em] uppercase font-bold text-white/50 mb-2.5">Recent developments</div>
            <ul className="space-y-2">
              {(g.recent_developments || []).map((x, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-white/65">
                  <span className="w-1 h-1 rounded-full bg-amber-300 mt-2 shrink-0" /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Panel>

      {/* Strategic intent */}
      <Panel className="p-7">
        <Kicker className="mb-4">Strategic intent &amp; capital deployment</Kicker>
        <p className="text-[14px] text-white/70 leading-relaxed mb-5 max-w-4xl">{si.stated_strategy}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            ["Capex allocation", si.capex_allocation],
            ["R&D allocation", si.rd_allocation],
            ["Acquisition activity", si.acquisition_activity],
            ["Market expansion", si.market_expansion],
            ["Technology investment", si.technology_investment],
            ["Sustainability investment", si.sustainability_investment],
            ["Talent investment", si.talent_investment],
          ]
            .filter(([, v]) => v)
            .map(([l, v]) => (
              <Glance key={l} label={l} value={v} />
            ))}
        </div>
      </Panel>

      {/* Snapshot of dimensions */}
      <Panel className="p-7">
        <Kicker className="mb-4">Dimension snapshot</Kicker>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {dims.map((d) => (
            <div key={d.key} className="rounded-lg border border-white/8 bg-[#060d18] p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-serif tabular-nums text-white text-2xl">
                  {d.score ?? "—"}
                </span>
                <RagBadge rag={d.rag} />
              </div>
              <div className="text-[12px] text-white/70 font-medium leading-snug mb-2">{d.name}</div>
              <div className="flex items-center justify-between">
                <TrendBadge trend={d.trend} />
                <ConfBadge level={d.confidence} />
              </div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
};
